import { cn } from "@fox1ck-org/ui";

/**
 * The running total by hour (доски Today, MToday): today to this hour, filled,
 * yesterday dashed over the whole day. Drawn in a fixed coordinate box that
 * stretches to the panel; labels are HTML on top, so they do not stretch.
 * `compact` (доска MToday): five hours under it and no scale beside it.
 */
export function RunChart({
  today,
  yesterday,
  fmt,
  nowLabel,
  wasLabel,
  endLabel,
  compact,
}: {
  today: number[];
  yesterday: number[];
  fmt: (n: number) => string;
  nowLabel: string;
  wasLabel: string;
  endLabel?: string;
  compact?: boolean;
}) {
  const W = 1000;
  const H = 400;
  const all = [...today, ...yesterday, 0];
  const lo = Math.min(...all);
  const hi = Math.max(...all);
  const pad = (hi - lo) * 0.08 || 1;
  const min = lo - pad;
  const max = hi + pad;
  // Bucket i is the total at the END of hour i, so it sits at (i + 1):00; the day starts at 0.
  const x = (i: number) => ((i + 1) / 24) * W;
  const hourX = (h: number) => (h / 24) * W;
  const y = (v: number) => H - ((v - min) / (max - min)) * H;
  const path = (arr: number[]) => `M0 ${y(0).toFixed(1)} ` + arr.map((v, i) => `L${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(" ");
  const last = today.length - 1;
  // Late in the day the labels go to the left of the «now» point.
  const flip = last > 15;
  const ticks = niceTicks(min, max);
  const pct = (v: number, of: number) => `${(v / of) * 100}%`;
  const yEnd = yesterday[yesterday.length - 1];
  return (
    <div className={cn("relative min-w-0 flex-1 pb-5", !compact && "pl-11")}>
      <div className="relative h-full w-full">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible" aria-hidden>
          {ticks.map((v) => (
            <line key={v} x1={0} x2={W} y1={y(v)} y2={y(v)} stroke="var(--border)" vectorEffect="non-scaling-stroke" />
          ))}
          <path d={path(yesterday)} fill="none" stroke="var(--muted-foreground)" strokeWidth={1.5} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
          {last >= 0 ? (
            <>
              <path d={`${path(today)} L${x(last)} ${y(0)} L0 ${y(0)} Z`} fill="var(--primary-soft)" />
              <path d={path(today)} fill="none" stroke="var(--primary)" strokeWidth={2} vectorEffect="non-scaling-stroke" />
            </>
          ) : null}
        </svg>
        {(compact ? [] : ticks).map((v) => (
          <span key={v} className="absolute -left-11 w-9 -translate-y-1/2 text-right font-mono text-xs tabular-nums text-muted-foreground" style={{ top: pct(y(v), H) }}>
            {fmt(v)}
          </span>
        ))}
        {(compact ? [0, 6, 12, 18, 24] : [0, 3, 6, 9, 12, 15, 18, 21, 24]).map((h) => (
          <span key={h} className={cn("absolute -bottom-5 w-10 font-mono text-xs tabular-nums text-muted-foreground", h === 24 ? "-translate-x-full text-right" : h === 0 && compact ? "text-left" : "-translate-x-1/2 text-center")} style={{ left: pct(hourX(h), W) }}>
            {compact ? String(h).padStart(2, "0") : `${String(h).padStart(2, "0")}:00`}
          </span>
        ))}
        {endLabel && yEnd !== undefined ? (
          <span className="absolute -translate-x-full -translate-y-[130%] whitespace-nowrap text-xs text-muted-foreground" style={{ left: "100%", top: pct(y(yEnd), H) }}>
            {endLabel}
          </span>
        ) : null}
        {last >= 0 ? (
          <>
            <span className="absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary" style={{ left: pct(x(last), W), top: pct(y(today[last]!), H) }} />
            <span className={cn("absolute -translate-y-[130%] whitespace-nowrap font-mono text-xs font-semibold", flip ? "-ml-2 -translate-x-full" : "ml-2")} style={{ left: pct(x(last), W), top: pct(y(today[last]!), H) }}>
              {nowLabel}
            </span>
            {wasLabel ? (
              <span className={cn("absolute mt-1 whitespace-nowrap font-mono text-xs text-muted-foreground", flip ? "-ml-2 -translate-x-full" : "ml-2")} style={{ left: pct(x(last), W), top: pct(y(yesterday[last] ?? 0), H) }}>
                {wasLabel}
              </span>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

/** Three or four round gridlines across [min, max]. Pure. */
export function niceTicks(min: number, max: number): number[] {
  const span = max - min;
  if (!(span > 0)) return [0];
  const raw = span / 3;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return out;
}
